import { fetchTradeAccounts } from "../../../services/brokerIntegrationApi";
import { listStrategyWorkspaces } from "../../../services/reportsApi";
import { resolveBrokerAccountWorkspaceId } from "./openBrokerAccountWorkspace";
import { workspaceCanvasPath } from "./workspaceRoutes";

/** `?new_report=<strategyId>` on an account canvas opens Create report under that strategy. */
export const WORKSPACE_NEW_REPORT_PARAM = "new_report";

/**
 * Where a report "from this strategy" is made: the account canvas the strategy sits on, with
 * Create report open under its node and the trades (a Launchpad run / backtest) preselected.
 * Prefers the active account's canvas; otherwise switches to the account of the first canvas.
 * Not on any canvas yet: the active account's canvas (the canvas adds the strategy node).
 * Null without an active account — the caller falls back to /reports/new.
 */
export async function strategyNewReportPath(
  strategyId: string,
  tradeSource: string | null,
  active: {
    brokerName: string | null;
    accountCode: string | null;
    setActiveTradeAccount: (brokerName: string, accountCode: string) => Promise<void>;
  }
): Promise<string | null> {
  const id = strategyId.trim();
  if (!id) return null;
  if (!active.brokerName || !active.accountCode) {
    // Pages outside the loaded account context (Launchpad sheet) — ask the server.
    const fromServer = (await fetchTradeAccounts().catch(() => null))?.active;
    if (fromServer?.broker_name && fromServer.account_code) {
      active = { ...active, brokerName: fromServer.broker_name.toLowerCase(), accountCode: fromServer.account_code };
    }
  }
  const { workspaces } = await listStrategyWorkspaces(id);
  const same = (a?: string | null, b?: string | null) => (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
  const onActive = workspaces.find(
    (w) => same(w.brokerAccountCode, active.accountCode) && (!w.brokerName || same(w.brokerName, active.brokerName))
  );
  const target = onActive ?? workspaces.find((w) => w.brokerName && w.brokerAccountCode);
  let workspaceId = target?.workspaceId ?? null;
  if (target && !onActive && target.brokerName && target.brokerAccountCode) {
    await active.setActiveTradeAccount(target.brokerName, target.brokerAccountCode);
  }
  if (!workspaceId && active.brokerName && active.accountCode) {
    workspaceId = await resolveBrokerAccountWorkspaceId({
      brokerName: active.brokerName,
      brokerAccountCode: active.accountCode,
    });
  }
  if (!workspaceId) return null;
  const params = new URLSearchParams({ [WORKSPACE_NEW_REPORT_PARAM]: id });
  if (tradeSource) params.set("trade_source", tradeSource);
  return `${workspaceCanvasPath(workspaceId)}?${params.toString()}`;
}
